import React, { useCallback, useContext, useMemo, useRef } from 'react';
import {
  Flex,
  Carousel,
  RemoteImage,
  useScreenSize,
  IconFullscreen,
  IconArrowLeft,
  ColorValues,
  IconArrowRight,
  type SliderRef,
  Colors,
} from '@banhala/ui-kit';
import Animated, {
  interpolate,
  type SharedValue,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { ScrollContext } from '../../context/ScrollContext';
import { RelatedGoodsView } from './components/RelatedGoodsView';
import { BlurBackground } from './components/BlurBackground';
import { isNotNil, isWeb } from 'shared/src/utils';
import { useGoodsBasic } from '../../queries/useGoodsBasic';
import { usePreRenderParams } from '../../hooks/usePreRenderParams';
import { DetailedImageViewer } from './components/DetailedImageViewer';
import { AiStudioRedirect } from './components/AiStudioRedirect';
import { PaginationDots } from './components/PaginationDots';

type CarouselItem =
  | {
      type: 'image';
      uri: string;
    }
  | {
      type: 'related';
    };

const CoverImageItem = React.memo(
  ({
    uri,
    width,
    index,
    currentIndexValue,
    scrollY,
    onPress,
  }: {
    uri: string;
    width: number;
    index: number;
    currentIndexValue: SharedValue<number>;
    scrollY?: SharedValue<number>;
    onPress: () => void;
  }) => {
    const style = useAnimatedStyle(() => {
      if (currentIndexValue.value !== index || !scrollY || scrollY.value > 0) {
        return { overflow: 'hidden' };
      }
      return {
        overflow: 'hidden',
        transform: [
          {
            scale: interpolate(
              scrollY.value,
              [-width * 0.5, 0],
              [2, 1],
              'clamp',
            ),
          },
        ],
      };
    });

    return (
      <Animated.View style={style}>
        <Flex
          width={width}
          height={width}
          testID={`상품대표이미지-${index}`}
          onPress={onPress}
        >
          <RemoteImage uri={uri} width={width} height={width} />
        </Flex>
      </Animated.View>
    );
  },
);

export const CoverImages: React.FC = () => {
  const { width } = useScreenSize();
  const sliderRef = useRef<SliderRef | null>(null);

  const { scrollY } = useContext(ScrollContext);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const currentIndexValue = useSharedValue(0);
  const { data: goodsBasic, isSuccess: successBasicData } = useGoodsBasic();
  const { data: goodsBasicSkeleton } = usePreRenderParams();
  const isScrollNow = useRef<boolean>(false);

  const containerStaticStyle = useMemo(
    () => ({
      position: 'relative' as const,
      height: width,
      width: width,
      overflow: 'hidden' as const,
    }),
    [width],
  );

  const containerStyle = useAnimatedStyle(() => {
    // parallax는 위로 당길 때(scrollY < 0)만 필요
    if (!scrollY || scrollY.value >= 0) return containerStaticStyle;

    return {
      position: 'relative',
      height: interpolate(
        scrollY.value,
        [-width, 0],
        [width * 2, width],
        'clamp',
      ),
      width: width,
      transform: [
        {
          translateY: interpolate(
            scrollY.value,
            [-width, 0],
            [-width, 0],
            'clamp',
          ),
        },
      ],
      overflow: 'hidden',
    };
  });
  const [isVisible, setIsVisible] = React.useState(false);
  // const scrollValue = useSharedValue(0);

  const carouselList = useMemo(() => {
    const images =
      isNotNil(goodsBasic) && isNotNil(goodsBasic.cover_images)
        ? goodsBasic?.cover_images
        : (goodsBasicSkeleton?.cover_images ?? []);

    return !successBasicData
      ? images.map(item => ({
          type: 'image' as const,
          uri: item,
        }))
      : [
          ...images.map(item => ({
            type: 'image' as const,
            uri: item,
          })),
          { type: 'related' as const },
        ];
  }, [goodsBasic, goodsBasicSkeleton?.cover_images, successBasicData]);

  const lastImage = useMemo(() => {
    if (!goodsBasic) return null;

    if (goodsBasic.cover_images.length > 0) {
      return goodsBasic.cover_images[goodsBasic.cover_images.length - 1];
    }
    return null;
  }, [goodsBasic]);

  const isShowArrow = useMemo(() => isWeb, []);

  const scrollX = useSharedValue(0);

  const onPressImage = useCallback(() => {
    if (isScrollNow.current) return;
    setIsVisible(true);
  }, []);

  const onScrollStart = useCallback(() => {
    isScrollNow.current = true;
  }, []);

  const onScrollEnd = useCallback(() => {
    isScrollNow.current = false;
  }, []);

  const onProgressChange = useCallback(
    (_: number, absoluteProgress: number) => {
      scrollX.value = absoluteProgress;
      const newIndex = Math.round(absoluteProgress);
      if (newIndex !== currentIndexValue.value) {
        currentIndexValue.value = newIndex;
        setCurrentIndex(newIndex);
      }
    },
    [currentIndexValue, scrollX],
  );

  const renderItem = useCallback(
    ({ item, index }: { item: CarouselItem; index: number }) => {
      if (item.type === 'image') {
        return (
          <CoverImageItem
            uri={item.uri}
            width={width}
            index={index}
            currentIndexValue={currentIndexValue}
            scrollY={scrollY}
            onPress={onPressImage}
          />
        );
      }

      return (
        <Flex testID={`상품대표이미지-추천상품`}>
          {isNotNil(lastImage) && (
            <RemoteImage
              uri={lastImage}
              width={width}
              height={width}
              blurRadius={10}
            />
          )}
          <BlurBackground />
          <RelatedGoodsView focused={currentIndex === index} />
        </Flex>
      );
    },
    [width, currentIndexValue, scrollY, onPressImage, lastImage, currentIndex],
  );

  return (
    <Animated.View style={containerStyle}>
      <Carousel<CarouselItem>
        sliderRef={sliderRef}
        style={{ backgroundColor: Colors.bg_secondary }}
        data={carouselList}
        renderItem={renderItem}
        loop={false}
        vertical={false}
        width={width}
        height={isWeb ? width : undefined}
        onScrollStart={onScrollStart}
        onScrollEnd={onScrollEnd}
        onProgressChange={onProgressChange}
      />
      {carouselList[currentIndex]?.type !== 'related' && (
        <Flex
          padding={{ horizontal: 16, vertical: 16 }}
          position={'absolute'}
          right={0}
          bottom={0}
          onPress={() => setIsVisible(true)}
        >
          <IconFullscreen fill={'white'} />
        </Flex>
      )}
      {currentIndex === 0 && (
        <Flex
          padding={{ horizontal: 16, vertical: 16 }}
          position={'absolute'}
          left={0}
          bottom={0}
        >
          <AiStudioRedirect />
        </Flex>
      )}

      {isShowArrow && currentIndex > 0 && (
        <Flex.Center top={0} bottom={0} left={0} position={'absolute'}>
          <Flex.Center
            width={40}
            height={40}
            backgroundColor={'bg_black_button'}
            backgroundColorOpacity={0.4}
            onPress={() => sliderRef.current?.prev()}
          >
            <IconArrowLeft size={24} fill={ColorValues.gray50} />
          </Flex.Center>
        </Flex.Center>
      )}

      {isShowArrow && currentIndex < carouselList.length - 1 && (
        <Flex.Center top={0} bottom={0} right={0} position={'absolute'}>
          <Flex.Center
            width={40}
            height={40}
            backgroundColor={'bg_black_button'}
            backgroundColorOpacity={0.4}
            onPress={() => sliderRef.current?.next()}
          >
            <IconArrowRight size={24} fill={ColorValues.gray50} />
          </Flex.Center>
        </Flex.Center>
      )}

      <Flex.Center position={'absolute'} left={0} right={0} bottom={16}>
        <PaginationDots
          value={currentIndex}
          scrollX={scrollX}
          maxCount={(goodsBasic?.cover_images ?? [0, 1]).length + 1}
        />
      </Flex.Center>

      {isNotNil(goodsBasic) && isNotNil(goodsBasic.cover_images) && (
        <DetailedImageViewer
          visible={isVisible}
          images={goodsBasic.cover_images}
          onCloseViewer={() => setIsVisible(false)}
          currentIndex={Math.min(
            currentIndex,
            goodsBasic.cover_images.length - 1,
          )}
        />
      )}
    </Animated.View>
  );
};
